/**
 * Attribution from the website's signup links (oppref + UTM).
 *
 * Before the visitor has decided: kept only for this tab (sessionStorage).
 * With permission: kept 30 days (like the pixel's __oppref cookie) and sent to the server,
 * which stores it on the account so the Stripe webhook can report payments later.
 * Rejected / withdrawn: deleted.
 */
import { AdsConsent, getAdsConsent, readPixelBrowserRef } from "./adsConsent";
import { ADS_TRACKING_ENABLED } from "./adsPixel";

const KEYS = ["oppref", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const SESSION_KEY = "ocs_ads_attr_pending";
const STORE_KEY = "ocs_ads_attr";
const TTL_MS = 30 * 86400000;

type Attribution = Record<string, string>;

const read = (storage: Storage, key: string): any => {
  try { return JSON.parse(storage.getItem(key) || "null"); } catch { return null; }
};

/** Read attribution parameters from the current URL (call on every route change). */
export const captureAttributionFromUrl = () => {
  if (!ADS_TRACKING_ENABLED) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const found: Attribution = {};
    KEYS.forEach((k) => {
      const v = params.get(k);
      if (v && v.length <= 512) found[k] = v;
    });
    if (!Object.keys(found).length) return;
    // landing page without query string (no personal data in the URL is kept)
    found.landing_url = window.location.origin + window.location.pathname;
    const record = { ...found, ts: Date.now() };
    const consent = getAdsConsent();
    if (consent === "granted") localStorage.setItem(STORE_KEY, JSON.stringify(record));
    else if (consent === "unknown") sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
    // denied: not kept at all
  } catch { /* storage unavailable */ }
};

/** Permission changed: promote or delete what was captured. */
export const applyConsentToAttribution = (consent: AdsConsent) => {
  try {
    if (consent === "granted") {
      const pending = read(sessionStorage, SESSION_KEY);
      if (pending) localStorage.setItem(STORE_KEY, JSON.stringify(pending));
      sessionStorage.removeItem(SESSION_KEY);
    } else if (consent === "denied") {
      sessionStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(STORE_KEY);
    }
  } catch { /* ignore */ }
};

/** Attribution for the server - only with permission. */
export const getAttributionForServer = (): Attribution | undefined => {
  if (getAdsConsent() !== "granted") return undefined;
  const out: Attribution = {};
  const rec = read(localStorage, STORE_KEY);
  if (rec && Date.now() - (rec.ts || 0) < TTL_MS) {
    KEYS.concat("landing_url").forEach((k) => { if (typeof rec[k] === "string") out[k] = rec[k]; });
  }
  const obref = readPixelBrowserRef();
  if (obref) out.obref = obref;
  return Object.keys(out).length ? out : undefined;
};

/** Payload added to signup requests (email + social). */
export const getAdsSignupPayload = () => {
  if (!ADS_TRACKING_ENABLED) return undefined;      // tracking switched off (e.g. QA)
  const consent = getAdsConsent();
  return {
    consent: consent === "unknown" ? undefined : consent,
    consent_source: "shared_cookie",
    attribution: getAttributionForServer(),
  };
};
